import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  getTargetingPresetList,
  ISimpleTargetingPreset,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import { IDNameTableModal } from '~/src/dash/components/modals/IDNameTableModal';
import { IItemIDNameRoute } from '~/src/dash/components/tables/IDNameTable/common';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  getComparator,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  ItemRouteType,
  navigate,
  RouteTargetingPresetNew,
} from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import {
  formatTargetingPresetResponseIntoLVTRows,
  ITargetingPresetAssociatedItem,
} from '~/src/dash/views/tools/TargetingPresets/config';
import { TopBar } from '~/src/dash/views/topbar/TopBar';

const queryData = (data: ISimpleTargetingPreset[], query: string) => {
  let queriedRows = cloneDeep(data);
  if (query !== '') {
    queriedRows = data.filter((row) => {
      for (const key of ['ID', 'Name']) {
        // @ts-ignore
        if (row[key].toString().toLowerCase().includes(query.toLowerCase())) {
          return true;
        }
      }
      return false;
    });
  }

  return queriedRows;
};

export const TargetingPresets = () => {
  const isMounted = useRef(false);
  const { session, user, isSuperUser } = useBaseStore();
  const { Tools } = useBaseConfig();
  const cfg = Tools.Targeting.Main;
  const { defaultState, table } = cfg;
  cfg.isInitialized = true;

  // states and refs
  const [responseData, setResponseData] = useState<ISimpleTargetingPreset[]>([]);
  const [queriedData, setQueriedData] = useState<ISimpleTargetingPreset[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [isTableActionPending, setIsTableActionPending] = useState(false);
  const [order, setOrder] = useState<Order>(defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy || '');
  const [page, setPage] = useState(defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);
  const [sortedRows, setSortedRows] = useState([] as IListViewTableRow[]);

  const [query, setQuery] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  const [openAssociatedDialog, setOpenAssociatedDialog] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState({} as ISimpleTargetingPreset);
  const [activeAssociatedType, setActiveAssociatedType] =
    useState<ITargetingPresetAssociatedItem | null>(null);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const queriedRows = queryData(responseData, query);
      unstable_batchedUpdates(() => {
        setQueriedData(queriedRows);
        setTotalRowCount(queriedRows.length);
        setPage(0);
      });
    }
  }, [responseData, query]);

  useEffect(() => {
    if (isMounted.current) {
      if (!isEmpty(queriedData)) {
        setSortedRows(
          stableSort(rows, getComparator(order, orderBy, table.columns[orderBy].valueType)),
        );
      } else {
        setSortedRows([]);
      }
    }
  }, [order, orderBy, queriedData]);

  useEffect(() => {
    if (isMounted.current) {
      defaultState.page = page;
      defaultState.order = order;
      defaultState.orderBy = orderBy;
    } else {
      isMounted.current = true;
    }
  }, [page, order, orderBy]);

  const getData = () => {
    setLoadState(LoadingStateLoading);
    getTargetingPresetList(session.publisher.ID, {})
      .then((res) => {
        unstable_batchedUpdates(() => {
          if (res) {
            setResponseData(res?.Presets || []);
            setTotalRowCount(res?.NumPresets || 0);
            setLoadState(!isEmpty(res?.Presets) ? LoadingStateHasData : LoadingStateNoData);
          }
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setSnackbarMessage('Error while loading presets. Please wait and try again');
          setSnackbarError(true);
          setResponseData([]);
          setTotalRowCount(0);
          setLoadState(LoadingStateNoData);
        });
      });
  };

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;

    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    });
    defaultState.rowsPerPage = newRowsPerPage;
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
    defaultState.page = newPage;
  };

  const handleOnChangeQuery = (text: string) => {
    setQuery(text);
  };

  const handleDeleteItem = (id: string) => {
    setIsTableActionPending(true);
    cfg
      .deletePresetFn(session.publisher.ID, id)
      .then(() => {
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage('Successfully deleted preset.');
          setSnackbarError(false);
        });
        getData();
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage('Error while deleting preset.');
          setSnackbarError(true);
        });
      });
    return new Promise((resolve) => resolve(undefined));
  };

  const handleClickOnAssociatedBidders = (id: string) => {
    const newSelectedPreset = responseData.find(({ ID }) => ID === Number(id));
    if (!isEmpty(newSelectedPreset)) {
      unstable_batchedUpdates(() => {
        setSelectedPreset(newSelectedPreset || {});
        setActiveAssociatedType(cfg.dialogs.bidder);
        setOpenAssociatedDialog(true);
      });
    }
    return;
  };

  const handleClickOnAssociatedCampaigns = (id: string) => {
    const newSelectedPreset = responseData.find(({ ID }) => ID === Number(id));
    if (!isEmpty(newSelectedPreset)) {
      unstable_batchedUpdates(() => {
        setSelectedPreset(newSelectedPreset || {});
        setActiveAssociatedType(cfg.dialogs.campaign);
        setOpenAssociatedDialog(true);
      });
    }
    return;
  };

  const handleCloseAssociatedDialog = () => {
    unstable_batchedUpdates(() => {
      setOpenAssociatedDialog(false);
      setSelectedPreset({} as ISimpleTargetingPreset);
      setActiveAssociatedType(null);
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  const rows = formatTargetingPresetResponseIntoLVTRows(queriedData, table);
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Panels max vertical relative minWidthMain>
      <Panel largeHorizontalPadding smallVerticalPadding>
        <TopBar title='Targeting Presets' enableUINotices />
      </Panel>
      <Panel
        largeHorizontalPadding
        smallVerticalPadding
        style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
      >
        <Cols gap={spacing.base} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Col flex>
            <Text extraLarge bold>
              Presets
            </Text>
          </Col>
          <Col>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <ButtonV2
                  small
                  onClick={() => {
                    track('Presets - Create New', { isSuperUser }, user.user);
                    navigate(RouteTargetingPresetNew, extractRouteParams({ session, user }));
                  }}
                >
                  + New Preset
                </ButtonV2>
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding marginTop={spacing.base}>
        <Rows gap={spacing.base}>
          <Row largeHorizontalPadding smallVerticalPadding style={highlightedDivStyle}>
            <Rows>
              <Row style={{ padding: '8px 0px' }}>
                <Cols style={{ justifyContent: 'space-between' }}>
                  <Col>
                    <Cols
                      wrap
                      gap={spacing.medium}
                      style={{
                        alignItems: 'center',
                        rowGap: spacing.small,
                      }}
                    >
                      <Col>
                        <Cols
                          wrap
                          gap={spacing.medium}
                          style={{
                            alignItems: 'center',
                            rowGap: spacing.small,
                          }}
                        >
                          <Col>
                            <TextInput
                              enableClear
                              handleOnChange={handleOnChangeQuery}
                              msDelay={500}
                              placeholder='Search by Name or ID'
                              prefix='search'
                            />
                          </Col>
                          <Col>
                            <SearchResultsText
                              numRowsDisplayed={responseData.length}
                              numTotalRows={totalRowCount}
                            />
                          </Col>
                        </Cols>
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Row>
              <Row noPadding marginTop={spacing.large}>
                <ListViewTable
                  className={cfg.cypressClassName}
                  loadState={loadState}
                  activeColumnKeys={defaultState.activeColumnKeys}
                  columns={table.columns}
                  customFunctions={{
                    handleClickOnAssociatedBidders,
                    handleClickOnAssociatedCampaigns,
                  }}
                  order={order}
                  orderBy={orderBy}
                  page={page}
                  rows={visibleRows}
                  rowsPerPage={rowsPerPage}
                  totalRowCount={totalRowCount}
                  handleOnClickColumnHeader={handleOnClickColumnHeader}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  isActionPending={isTableActionPending}
                  isExpanded={false}
                  isReadAuthorized={true}
                  isWriteAuthorized={true}
                  enableActions
                  handleDeleteItem={handleDeleteItem}
                  handleCloneItem={cfg.getHandleClone(session, user)}
                  getItemSettingsRoute={cfg.getSettingsRouteFn(session, user)}
                  customDeleteMessage={cfg.customDeleteMessage}
                  key={cfg.cypressClassName}
                />
              </Row>
            </Rows>
          </Row>
        </Rows>
      </Panel>
      {/* ASSOCIATED MODAL */}
      {!isEmpty(selectedPreset?.Bidders) && !isEmpty(activeAssociatedType) && (
        <IDNameTableModal
          open={openAssociatedDialog}
          onClose={handleCloseAssociatedDialog}
          title={activeAssociatedType.title}
          subTitle={selectedPreset.Name}
          customFunctions={{ handleCloseAssociatedDialog }}
          data={(selectedPreset[activeAssociatedType.dataKey] as IItemIDNameRoute[]) || []}
          type={activeAssociatedType.dataKey as ItemRouteType}
        />
      )}
      <SnackbarView open={!!snackbarMessage} error={!!snackbarError} onClose={handleSnackbarClose}>
        {snackbarMessage}
      </SnackbarView>
    </Panels>
  );
};
